/**
 * Firebase clients for the CRM — two of them, on purpose.
 *
 * The CRM owns `skida-internal`. Everything it writes, and its whole identity
 * model, lives there. But the questions it exists to answer are about product
 * data — who has a Skida account, which company are they in, how many routes
 * have they drawn — and that data stays in the product's project, where it
 * belongs and where the CRM has never had any business writing.
 *
 * So the browser runs two Firebase apps side by side:
 *
 *   `db`         → skida-internal        read + write, the CRM's own data
 *   `productDb`  → learned-spider-411409 read only, never written to
 *
 * ## Why this is not two sets of credentials
 *
 * The obvious way to authenticate against a second project is a custom token,
 * which has to be signed by a service account belonging to that project — so
 * the key would have to be stored somewhere in ours. That spreads a credential
 * rather than containing it, and it is not necessary here.
 *
 * Instead the *same Google account* signs into both. Signing in to the CRM
 * yields a Google OAuth credential; `signInToProduct` replays it against the
 * product app. No popup, no key, no secret. The identity the product project
 * sees is the person, not a robot, which is exactly what its rules already key
 * on — a verified `@mountainsnow.ski` address.
 *
 * See MIGRATION.md.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signOut,
  type Auth,
  type AuthCredential,
  type UserCredential,
} from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'

const REQUIRED_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
  // The product project. Only what a read client needs — no storage bucket,
  // no messaging sender.
  'VITE_PRODUCT_API_KEY',
  'VITE_PRODUCT_AUTH_DOMAIN',
  'VITE_PRODUCT_PROJECT_ID',
  'VITE_PRODUCT_APP_ID',
] as const

const missing = REQUIRED_KEYS.filter((key) => !import.meta.env[key])
if (missing.length > 0) {
  throw new Error(
    `Missing Firebase config: ${missing.join(', ')}. ` +
      'Copy .env.example to .env and fill in both projects (see SETUP.md).',
  )
}

const crmConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const productConfig = {
  apiKey: import.meta.env.VITE_PRODUCT_API_KEY,
  authDomain: import.meta.env.VITE_PRODUCT_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_PRODUCT_PROJECT_ID,
  appId: import.meta.env.VITE_PRODUCT_APP_ID,
}

/** The CRM's own project. Named so it can never collide on the same origin. */
export const app: FirebaseApp = initializeApp(crmConfig, 'skida-internal')
export const auth: Auth = getAuth(app)
export const db: Firestore = getFirestore(app)

/**
 * The product project, read-only.
 *
 * Nothing in this codebase may call `setDoc`, `addDoc`, `updateDoc` or
 * `deleteDoc` against `productDb`. That is not enforced by types — Firestore's
 * API takes any `Firestore` handle — so it is enforced by the product project's
 * own rules, which grant read and nothing else. This comment is the reminder;
 * the rules are the guarantee.
 */
export const productApp: FirebaseApp = initializeApp(productConfig, 'skida-product')
export const productAuth: Auth = getAuth(productApp)
export const productDb: Firestore = getFirestore(productApp)

/**
 * Sign the already-signed-in person into the product project too.
 *
 * Called once, right after the CRM sign-in succeeds, with the OAuth credential
 * that sign-in produced. The user sees one popup, not two — the credential is
 * simply replayed against the second app.
 *
 * Failure here is deliberately not fatal. The CRM's own data is in the CRM's
 * own project, so leads, events and the pipeline all work regardless; what
 * degrades is the product-backed columns, which already render "—" when a read
 * is refused. Breaking the whole app because a secondary read is unavailable
 * would be a worse trade than showing a dash.
 *
 * On a page reload nothing calls this: Firebase persists a session per app, so
 * both restore on their own.
 */
export async function signInToProduct(
  credential: AuthCredential | null,
): Promise<UserCredential | null> {
  // Two different failures land here and they need different fixes, so they are
  // reported differently rather than both becoming a blank column.
  if (!credential) {
    console.error(
      '[crm] No OAuth credential came back from sign-in, so the product project ' +
        'was never contacted. Product-backed columns will be blank.',
    )
    return null
  }
  try {
    const result = await signInWithCredential(productAuth, credential)
    console.info('[crm] Signed in to the product project as', result.user.email)
    return result
  } catch (err) {
    // Expected, and not an error worth shouting about. A Google credential
    // carries the audience of the OAuth client that minted it, and a Firebase
    // project only accepts tokens minted by its own clients. skida-internal and
    // the product project have different clients, so replaying one into the
    // other fails with `access_token audience is not for this project`.
    //
    // Whitelisting one client in the other project would fix it, but that is a
    // setting inside the product project's auth config, and `connectProduct`
    // needs nothing from anybody. The attempt stays because it costs one call
    // and would start working the day such a whitelist exists.
    console.info(
      '[crm] Credential replay into the product project was refused ' +
        `(${(err as { code?: string }).code ?? 'unknown'}). Falling back to an ` +
        'explicit connect.',
    )
    return null
  }
}

/**
 * Sign in to the product project explicitly, from a real click.
 *
 * This is the reliable route, because the popup runs against the product
 * project's *own* OAuth client — nothing has to trust anything. The cost is one
 * click, once, and Firebase persists the session per app afterwards, so a
 * reload restores it without asking again.
 *
 * It must be called from a user gesture. A popup opened after an await is
 * blocked by the browser, which is exactly why this is not chained onto the
 * CRM sign-in.
 */
export async function connectProduct(chooseAccount = false): Promise<UserCredential> {
  const provider = new GoogleAuthProvider()
  // Skip the account chooser when there is only one plausible answer: the
  // person is already signed in to the CRM as somebody. `chooseAccount` shows
  // it anyway — the way out when the wrong account was connected (23 Oct 2026).
  const email = auth.currentUser?.email
  provider.setCustomParameters({
    ...(email ? { login_hint: email } : {}),
    ...(chooseAccount ? { prompt: 'select_account' } : {}),
  })
  return signInWithPopup(productAuth, provider)
}

/** Fires whenever the product session appears or disappears. */
export function onProductAuthChanged(cb: (signedIn: boolean, email: string | null) => void): () => void {
  return onAuthStateChanged(productAuth, (u) => cb(u !== null, u?.email ?? null))
}

/** Sign out of both projects. Leaving the product session behind would be a leak. */
export async function signOutEverywhere(): Promise<void> {
  await Promise.allSettled([signOut(auth), signOut(productAuth)])
}
